import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AxiosError } from 'axios';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { debounce } from '@mui/material/utils';
import { Set } from 'immutable';
import { pipe, Array as A } from 'effect';

import { updateQueryAction, querySelector, completeResetAction, locationSelector } from '@/domain/router';
import { navigate } from '@/domain/router/redux/reduxActions';
import { SEARCH_MIN_CHARACTERS, queryToSearch, cleanQuery } from '@/domain/router/helpers';
import { documentsGridFilterAppliedAction } from '@/domain/documents';
import { useSaveSearchFavoriteMutation } from '@/domain/documents/queries';
import { makeSearchQuery } from '@/domain/documents/searchHelpers';
import { gridCurrentFiltersSelector } from '@/domain/documents/documentSelector';
import { getCurrentCompanyData, isGridWorkSpaceSelector } from '@/domain/companies';
import { chatAllUsersByIdSelector } from '@/domain/chat';
import { userSelector } from '@/domain/env/envSelector';
import { companyRoleSelector } from '@/domain/env/sessionRoleSelectors';
import usePrevious from '@/hooks/usePrevious';
import useToggle from '@/hooks/useToggle';
import toast from '@/components/Toast';

import {
  CLEARED_WORKSPACE_QUERY,
  workspaceSearchQuerySchema,
} from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/querySchema';
import { deriveQueryChips, type QueryChip } from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/chips';

const TAG_PREFIX = '#';

// `querySelector` is a hot path; parse defensively and fall back to an empty query
// rather than throw on an unexpected shape (e.g. a hand-edited URL).
const parseQuery = (raw: unknown) => {
  const result = workspaceSearchQuerySchema.safeParse(raw);
  return result.success ? result.data : {};
};

const useWorkspaceSearch = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { documentType, companyId } = useParams();

  const { userGUID } = useSelector(userSelector);
  // The favorite's filter is evaluated per company, so it follows the role there.
  const role = useSelector(companyRoleSelector);
  const saveFavorite = useSaveSearchFavoriteMutation(companyId ?? '');

  const rawQuery = useSelector(querySelector);
  const location = useSelector(locationSelector);
  const query = useMemo(() => parseQuery(rawQuery), [rawQuery]);
  const hasStag = (query.stag?.length ?? 0) > 0;
  const prevHasStag = usePrevious(hasStag);

  const gridFiltersRaw = useSelector(gridCurrentFiltersSelector) as Record<string, unknown>;
  const isGridWorkspace = useSelector(isGridWorkSpaceSelector);
  const currentCompany = useSelector(getCurrentCompanyData);
  const usersByID = useSelector(chatAllUsersByIdSelector);

  // Grid-column filters only count on the grid workspace (mirrors legacy gating).
  const gridFilters = useMemo(
    () => (isGridWorkspace && Object.keys(gridFiltersRaw).length > 0 ? gridFiltersRaw : {}),
    [isGridWorkspace, gridFiltersRaw],
  );

  const queryChips = useMemo(() => deriveQueryChips(query, currentCompany?.dateFormat), [query, currentCompany]);

  // ── free-text search (re-homed from Company/components/Search.jsx) ──────────────
  // TODO(location-lag): seeded from the store's copy of the location, which lags the router after a
  // navigation outside a React event: after browser back to a `?stag[]=` page the box stays empty,
  // or keeps the previous term, while the URL's filter is applied.
  const [inputValue, setInputValue] = useState(() => (query.stag?.length ? query.stag.join(' ') : ''));

  const search = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      dispatch(updateQueryAction({ stag: trimmed ? [trimmed] : undefined }));
    },
    [dispatch],
  );
  const debounceSearch = useMemo(() => debounce(search, 500), [search]);
  useEffect(() => () => debounceSearch.clear(), [debounceSearch]);

  const onInputChange = useCallback(
    (value: string) => {
      if (!value.startsWith(TAG_PREFIX) && SEARCH_MIN_CHARACTERS <= value.length) {
        debounceSearch(value);
      } else if (inputValue.length > value.length) {
        // Dropping below the threshold removes the applied term: sub-threshold input
        // never triggers a request, so this is the only way to clear `stag` from the URL.
        debounceSearch('');
      }
      setInputValue(value);
    },
    [debounceSearch, inputValue],
  );

  const onSelectSuggestion = useCallback(
    (suggestion: string) => {
      const trimmed = suggestion.trim();
      if (trimmed.replace(/ /g, '').length < 1) return;
      // Cancel any pending free-text search: the input is being committed as a chip, so a
      // trailing debounced `search` would re-add the same term as `stag` alongside `text`.
      debounceSearch.clear();
      const text = Set(query.text ?? [])
        .add(trimmed)
        .toJS() as string[];
      dispatch(updateQueryAction({ text, stag: undefined }));
      setInputValue('');
    },
    [dispatch, query.text, debounceSearch],
  );

  // External `stag` removal (e.g. a "reset" elsewhere) clears the box — but only when
  // it held a full term, so deleting 4→2 chars one-by-one keeps the 2-char leftover.
  useEffect(() => {
    if (prevHasStag && !hasStag && inputValue.length >= SEARCH_MIN_CHARACTERS) {
      setInputValue('');
    }
  }, [hasStag, prevHasStag, inputValue]);

  const displayThresholdWarning =
    inputValue.length > 0 && inputValue.length < SEARCH_MIN_CHARACTERS && !inputValue.startsWith(TAG_PREFIX);

  // ── per-chip removal (FIX DA-19657 §1.5: write only the chip's own key; the legacy
  //    handler also clobbered the independent `stag` free-text key) ────────────────
  const removeQueryChip = useCallback(
    (chip: QueryChip) => {
      const remaining = pipe(
        queryChips,
        A.filter((c) => c.type === chip.type && c.value !== chip.value),
        A.map((c) => c.value),
      );
      dispatch(
        updateQueryAction(chip.type === 'label' ? { label: remaining[0], query: null } : { [chip.type]: remaining }),
      );
    },
    [dispatch, queryChips],
  );

  const removeGridFilter = useCallback(
    (colId: string) => {
      const { [colId]: _removed, ...filters } = gridFilters;
      dispatch(documentsGridFilterAppliedAction({ filters }));
    },
    [dispatch, gridFilters],
  );

  // ── clear-all: query-tags + free-text + grid filters, in a SINGLE React commit ──
  // Grid filters live in Redux (sync reducer) while the query lives in the URL (async via the
  // `updateQuery` saga → navigate → LOCATION_CHANGE). Two plain dispatches land in two commits,
  // so the chips vanish in two visible waves. Instead navigate directly here — mirroring the
  // saga's completeReset + guarded push — and wrap it with the grid clear in `batch()`, so both
  // store writes happen in one synchronous stack and React commits them together.
  const clearAll = useCallback(() => {
    // A pending debounced free-text search would re-add `stag` right after we clear — cancel it.
    debounceSearch.clear();
    const previousSearch = queryToSearch(cleanQuery({ ...rawQuery }));
    const search = queryToSearch(cleanQuery({ ...rawQuery, ...CLEARED_WORKSPACE_QUERY }));
    batch(() => {
      if (Object.keys(gridFilters).length > 0) dispatch(documentsGridFilterAppliedAction({ filters: {} }));
      dispatch(completeResetAction());
      if (search !== previousSearch) dispatch(navigate.push({ pathname: location.pathname, search }));
    });
    setInputValue('');
  }, [dispatch, gridFilters, rawQuery, location, debounceSearch]);

  const hasActiveFilters = queryChips.length > 0 || Object.keys(gridFilters).length > 0;
  // Favorites persist only the global query (`makeSearchQuery`/`queryToSearch`), never grid
  // filters — so the save-to-favorite star gates on global chips alone, not `hasActiveFilters`.
  const canSaveFavorite = queryChips.length > 0;

  // ── save-to-favorite (parity with legacy useFavoriteTags.handleSaveTags) ────────
  const [isOpenModal, toggleModal] = useToggle();
  const [errorMeta, setErrorMeta] = useState<unknown>();
  const resetErrorMeta = useCallback(() => setErrorMeta(null), []);
  const handleToggleModal = useCallback(() => {
    toggleModal();
    setErrorMeta(null);
  }, [toggleModal]);

  const handleSaveTags = useCallback(
    (label: string) => {
      saveFavorite
        .mutateAsync({
          body: {
            label,
            filter: makeSearchQuery({ query, params: { documentType, user: { userId: userGUID, role } } }),
            query: `${documentType || ''}?${queryToSearch(query)}`,
          },
        })
        .then(() => {
          toast.info(
            intl.formatMessage({
              id: 'documents.tags.favorite.popup',
              defaultMessage: 'Search query was added to Favourite',
            }),
          );
          handleToggleModal();
        })
        .catch((error: AxiosError<string>) => {
          if (error.response?.status === 406) {
            toast.info(
              intl.formatMessage({
                id: `components.saveSearchPopup.error.${error.response?.statusText}`,
                defaultMessage: error.response?.data,
              }),
            );
            handleToggleModal();
          } else {
            setErrorMeta(error);
          }
        });
    },
    [saveFavorite, query, documentType, userGUID, role, intl, handleToggleModal],
  );

  const checkUserTag = useCallback((tagValue: string) => usersByID.get(tagValue.replace(/^#+/, '')), [usersByID]);

  const modalTitle = useMemo(() => {
    if (!queryChips.length) return '';
    const first = queryChips[0].value.replace(/^#*/gi, '');
    const user = checkUserTag(first);
    return user ? user.username : first;
  }, [queryChips, checkUserTag]);

  return {
    inputValue,
    onInputChange,
    onSelectSuggestion,
    displayThresholdWarning,
    queryChips,
    removeQueryChip,
    gridFilters,
    removeGridFilter,
    clearAll,
    hasActiveFilters,
    canSaveFavorite,
    checkUserTag,
    favorite: { isOpenModal, handleToggleModal, handleSaveTags, modalTitle, errorMeta, resetErrorMeta },
  };
};

export default useWorkspaceSearch;
